import type { LeadIntakePayload, LeadIntakeResult } from './lead-intake';
import type { Client, Customer } from '@/utils/types';

// Formulario personalizado del builder ("Buscamos por ti" con campos propios):
// a qué cliente va y cómo se guarda el lead.
//
// Todo lead cuelga de un cliente (leads.customer_id → customers). Un lead sin
// cliente no se ve en el CRM, así que NUNCA se inserta con customer_id null:
//   1. con email → initializeCustomer (dedup por email en la edge);
//   2. si no hubo cliente y hay algún dato (nombre, apellido, teléfono, RUT o
//      email) → public-lead-intake CON el lead: la edge resuelve el cliente (con
//      solo el email crea el prospecto) y crea el lead en la misma llamada;
//   3. con cliente y sin lead todavía → insert directo del lead;
//   4. sin ningún dato de contacto → no hay lead.
// Nunca lanza: el correo a la automotora sale SIEMPRE después de esto, se haya
// guardado el lead o no (antes un error acá cortaba todo y no llegaba nada).
//
// Separado del componente para poder probarlo en node
// (scripts/leadClienteHarness).

export interface DatosFormularioPersonalizado {
  clientId: Client['id'];
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  rut: string;
  /** Columnas del lead (type, brand_id, model_id, search_params…), sin cliente. */
  leadFields: Record<string, unknown>;
}

export interface DepsFormularioPersonalizado {
  initializeCustomer: (datos: Partial<Customer>) => Promise<Pick<Customer, 'id'> | null | undefined>;
  submitLeadIntake: (payload: LeadIntakePayload) => Promise<LeadIntakeResult>;
  insertarLead: (fila: Record<string, unknown>) => PromiseLike<{ error: unknown }>;
  /** Para reportar sin romper; por defecto console.error. */
  reportar?: (mensaje: string, detalle: unknown) => void;
}

export interface ResultadoFormularioPersonalizado {
  customerId?: number | string;
  leadGuardado: boolean;
}

export async function guardarLeadFormularioPersonalizado(
  deps: DepsFormularioPersonalizado,
  datos: DatosFormularioPersonalizado
): Promise<ResultadoFormularioPersonalizado> {
  const reportar = deps.reportar ?? ((m: string, d: unknown) => console.error(m, d));
  const { clientId, email, phone, firstName, lastName, rut, leadFields } = datos;
  let customerId: number | string | undefined;
  let leadGuardado = false;

  if (email) {
    try {
      const customer = await deps.initializeCustomer({
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        email,
        phone: phone || undefined,
        rut: rut || undefined,
        client_id: clientId,
      });
      customerId = customer?.id ?? undefined;
    } catch {
      // Sin cliente todavía (p. ej. solo email → needs_details): lo intenta la edge abajo.
    }
  }

  if (!customerId && (firstName || lastName || phone || rut || email)) {
    try {
      // Server-side (la RLS bloquea el insert().select() anónimo en customers).
      const intake = await deps.submitLeadIntake({
        clientId,
        email: email || undefined,
        customer: {
          first_name: firstName || undefined,
          last_name: lastName || undefined,
          phone: phone || undefined,
          rut: rut || undefined,
        },
        lead: leadFields,
      });
      customerId = intake?.customer_id ?? undefined;
      leadGuardado = intake?.lead_id != null;
    } catch (err) {
      // La edge responde 400 'lead requires customer' si no hay a quién colgarlo.
      reportar('Formulario personalizado: no se pudo registrar el cliente', err);
    }
  }

  if (customerId && !leadGuardado) {
    try {
      const { error } = await deps.insertarLead({
        ...leadFields,
        client_id: clientId,
        customer_id: customerId,
        status: 'pending',
      });
      if (error) reportar('Formulario personalizado: no se pudo guardar el lead', error);
      else leadGuardado = true;
    } catch (err) {
      reportar('Formulario personalizado: no se pudo guardar el lead', err);
    }
  }

  return { customerId, leadGuardado };
}
